/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import IconLayout from './icon'
import IconAfterTextLayout from './icon-after-text-layout'
import IconBeforeTextLayout from './icon-before-text-layout'
import IconBothEndsLayout from './icon-both-ends-layout'
import IconTopOfTextLayout from './icon-top-of-text-layout'
import type { ButtonProps } from './types'
import {
  createUseStyledComponent,
  useTrueAtMediaBreakpointLessThen
} from '../../hooks'
import type { PrivateThemeUiProps } from '../../styles-system/create-component'
import { THEME_UI_DEFAULT_KEY } from '../../theme-provider/types'
import type { Theme } from '../../theme-provider/types'
import { useSafeThemeUI } from '../../theme-provider/use-theme-ui'
import extractStringNodes from '../../utils/extract-string-nodes'
import { EMPTY_OBJECT, get, omit, pick } from '../../utils/object'
import {
  containsOmitPropSymbol,
  getLayout,
  getPaddingX,
  getPaddingY,
  omitLayout,
  omitPaddingX,
  omitPaddingY,
  propValueIsSet,
  spacePropNames
} from '../../utils/props-utils'
import sizeSynchronize from '../../utils/size-synchronize'
import { resolveSize, toBaseUnit } from '../../utils/size-utils'
import Box from '../_primitives/box'
import LoadingSpinner from '../_primitives/progress/loading-spinner'
import Text from '../_primitives/text/text'
import type { TextProps } from '../_primitives/text/types'

const IconTopOfTextLayoutMemo = React.memo(IconTopOfTextLayout)
const IconBeforeTextLayoutMemo = React.memo(IconBeforeTextLayout)
const IconAfterTextLayoutMemo = React.memo(IconAfterTextLayout)
const IconBothEndsLayoutMemo = React.memo(IconBothEndsLayout)
const IconLayoutMemo = React.memo(IconLayout)

const useStyledComponent = createUseStyledComponent(
  ['button', 'a', 'li'],
  'csg-button'
)

export const ButtonPrivate = React.forwardRef<
  HTMLElement,
  ButtonProps &
    Omit<PrivateThemeUiProps, 'size'> & {
      theme?: Theme
      selected?: boolean
    }
>(function _ButtonPrivate(props, ref) {
  const theme = useSafeThemeUI(props)
  const { baseUnit = 'px' } = theme

  const {
    as: component = 'button',
    role = 'button',
    className,
    disabled,
    size,
    icon: iconProps,
    iconBefore,
    iconAfter,
    iconTop,
    children,
    variant = THEME_UI_DEFAULT_KEY,
    removeTextAt,
    lhCrop,
    textWrap,
    alignItems = 'center',
    justifyContent = 'center',
    iconSize: iconSizeProps,
    useIconsSize,
    toggled,
    isLoading,
    textProps = EMPTY_OBJECT as TextProps,
    __themeKey = 'buttons',
    __css = EMPTY_OBJECT,
    sx = EMPTY_OBJECT,
    'automation-id': automationId,
    hidden,
    ...rest
  } = props

  const Component = useStyledComponent(component)
  // reconcile deprecated iconTop props
  const icon = iconProps ?? iconTop
  const hasIcon = icon || iconBefore || iconAfter

  const { sx: sxTextProps, ...restTextProps } = textProps

  const removeText = removeTextAt
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useTrueAtMediaBreakpointLessThen(removeTextAt)
    : false

  const stringNodes = extractStringNodes(
    React.Children.toArray(children).filter(i => typeof i === 'string')
  )

  const renderNodes = React.Children.toArray(children).filter(
    i =>
      typeof i !== 'string' &&
      typeof i !== 'undefined' &&
      typeof i !== 'number' &&
      typeof i !== 'boolean'
  )

  const buttonLabel = `${stringNodes}`

  const { fontSize, spinnerSize, isIconOnly, iconSize } = useMemo(() => {
    const isIconOnly =
      (icon && (!buttonLabel || removeText)) || renderNodes.length > 0
    // this routine should be deprecated as we move away from using internal icon from theme-ui
    // to using icon from @costar/theme-ui-icon library. it's no longer necessary and size of icon
    // should be scale using combination of scale css and em unit depending on the icon.
    const { fontSize, spinnerSize, iconSize } = sizeSynchronize(
      size ?? 'sm', // default to standard 1rem if not provide for default calculation
      theme,
      isIconOnly ? 0 : icon ? +1 : -1
    )
    return {
      // don't include fontSize if size we not explicitly provided, this allow font size inheritances base on context
      ...(size ? { fontSize } : {}),
      spinnerSize,
      iconSize,
      isIconOnly,
      ...(iconSizeProps
        ? {
            iconSize: resolveSize(iconSizeProps, get(theme, 'sizes.icon'))
          }
        : {})
    }
  }, [
    buttonLabel,
    icon,
    iconSizeProps,
    removeText,
    renderNodes.length,
    size,
    theme
  ])

  const combineFontSize = {
    ...{ fontSize },
    ...pick(rest, ['fontSize']),
    ...pick(__css as any, ['fontSize']),
    ...pick(sx as any, ['fontSize'])
  }

  // remove size from layout fields
  const combineLayout = omit(
    {
      ...(useIconsSize || (icon && buttonLabel) || icon
        ? {}
        : { height: '2em' }), // default height with text button
      ...getLayout(props),
      ...getLayout(__css),
      ...getLayout(sx)
    },
    ['size']
  )

  const gapProps = {
    ...{
      gap: icon && !isIconOnly ? 'xxs' : 'xs'
    },
    ...pick(combineLayout, ['gap'])
  }

  // setting of padding props supersede em padding settings
  const combinePadding = {
    ...getPaddingX(__css),
    ...getPaddingY(__css),
    ...getPaddingX(props),
    ...getPaddingY(props),
    ...getPaddingX(sx),
    ...getPaddingY(sx)
  }
  const paddingProps = containsOmitPropSymbol(combinePadding)
    ? {}
    : propValueIsSet(combinePadding, spacePropNames)
      ? combinePadding
      : {
          emPaddingX: isIconOnly ? 'sm' : 'xs',
          ...(isIconOnly
            ? { emPaddingY: '0.4375em' }
            : Object.keys(combineLayout).filter(
                  k => k.toLowerCase().includes('height') && !!combineLayout[k]
                ).length > 0
              ? {}
              : { emPaddingY: 'xs-2' }),
          //Edge Case: 0.4375em is required for icon only buttons
          //in order to keep the height consistent with button/input/dropdowns
          ...combinePadding
        }

  const buttonSize = (
    useIconsSize && icon && !buttonLabel
      ? {
          height: iconSize,
          width: iconSize,
          ...toBaseUnit(combineLayout, baseUnit)
        }
      : toBaseUnit(combineLayout, baseUnit)
  ) as Record<string, string | string[]>

  const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: iconBefore || iconAfter ? 'space-between' : justifyContent,
    position: 'relative',
    ...(hasIcon ? gapProps : {}),
    ...pick(combineLayout, ['display'])
  }

  function wrapSvg(children: React.ReactNode) {
    const shouldWrap =
      children &&
      (children as any).type &&
      (children as any).type.name &&
      ((children as any).type.name as string).startsWith('Svg')

    function wrap() {
      const squareSize = resolveSize(iconSize, get(theme, 'sizes.icon'))
      return (
        <Box
          height={props.height ? props.height : squareSize}
          width={props.width ? props.width : squareSize}
        >
          {children}
        </Box>
      )
    }
    return shouldWrap ? wrap() : children
  }

  const textSx = useMemo(
    () => ({
      '&.is-hidden': {
        opacity: 0
      },
      ...sxTextProps
    }),
    [sxTextProps]
  )

  const child =
    icon && !isIconOnly ? (
      <IconTopOfTextLayoutMemo
        {...{
          role: component === 'button' ? role : undefined,
          buttonLabel,
          removeText,
          alignItems,
          icon,
          lhCrop,
          textWrap,
          iconSize,
          stringNodes,
          children,
          textProps,
          isLoading
        }}
      />
    ) : icon && isIconOnly ? (
      <IconLayoutMemo
        {...{
          icon,
          iconSize,
          isLoading
        }}
      />
    ) : iconBefore && !iconAfter ? (
      <IconBeforeTextLayoutMemo
        {...{
          role: component === 'button' ? role : undefined,
          buttonLabel,
          alignItems,
          iconBefore,
          lhCrop,
          textWrap,
          iconSize,
          stringNodes,
          children,
          textProps,
          isLoading
        }}
      />
    ) : iconAfter && !iconBefore ? (
      <IconAfterTextLayoutMemo
        {...{
          role: component === 'button' ? role : undefined,
          buttonLabel,
          alignItems,
          iconAfter,
          lhCrop,
          textWrap,
          iconSize,
          stringNodes,
          children,
          textProps,
          isLoading
        }}
      />
    ) : iconAfter && iconBefore ? (
      <IconBothEndsLayoutMemo
        {...{
          role: component === 'button' ? role : undefined,
          buttonLabel,
          alignItems,
          iconBefore,
          iconAfter,
          lhCrop,
          textWrap,
          iconSize,
          stringNodes,
          children,
          textProps,
          isLoading
        }}
      />
    ) : stringNodes.length > 0 ? (
      <Text
        className={classNames({
          'is-hidden': isLoading
        })}
        lhCrop={lhCrop}
        ellipsis={!textWrap}
        content={`${stringNodes}`}
        role={component === 'button' ? role : undefined}
        sx={textSx}
        {...restTextProps}
      >
        {stringNodes}
      </Text>
    ) : (
      wrapSvg(children)
    )

  return (
    <Component
      ref={ref}
      __themeKey={__themeKey}
      variant={variant}
      a11yLabel={buttonLabel}
      className={classNames(
        {
          'csg-tui-button': true,
          'is-toggled': toggled,
          'is-hidden': hidden
        },
        className
      )}
      {...{
        resetCss: {
          appearance: 'none',
          textDecoration: 'none',
          hyphens: 'auto',
          outline: 'unset'
        },
        'automation-id': automationId,
        ...(component === 'button'
          ? { type: 'button', disabled }
          : { role: role }),
        ...(component !== 'button' ? { 'aria-disabled': disabled } : {}),
        ...combineFontSize,
        ...paddingProps,
        ...(icon && !isIconOnly ? { flexDirection: 'column' } : {}),
        __css: {
          ...buttonStyle,
          ...omitPaddingX(
            omitPaddingY(omitLayout(omit(__css as any, ['fontSize', 'gap'])))
          )
        },
        sx: omit(sx as any, ['fontSize', 'gap']),
        ...omitPaddingX(
          omitPaddingY(omitLayout(omit(rest, ['fontSize', 'gap'])))
        ),
        ...omit(buttonSize, ['display'])
      }}
    >
      {isLoading ? (
        <>
          <LoadingSpinner
            size={spinnerSize}
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          />
          {child}
        </>
      ) : (
        child
      )}
    </Component>
  )
})

const MemoButton = React.memo(ButtonPrivate)

const Button: FC<ButtonProps> = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(function Button(props, ref) {
  return <MemoButton ref={ref} {...props} />
})

export default Button
